/**
 * Kiểu dữ liệu bài học theo khối — gói nội dung F-Future `ffuture.lesson/1`
 * (backend `apps/catalog/services/content_pack.py`, spec 2026-10-27-content-pack-blocks-design).
 *
 * Media trong payload đã được importer thay bằng URL phát (`audio`) + key bất biến (`audio_key`).
 */

export const CONTENT_LESSON_SCHEMA_PREFIX = "ffuture.lesson/";

/** Đối tượng từ dùng lại ở nhiều khối. `td` = thanh đọc thực tế (0 = nhẹ, 21 = nửa thanh 3). */
export interface WordObj {
  chu: string;
  phon?: string;
  py?: string[];
  tg?: number[];
  td?: number[];
  hv?: string;
  tu_loai?: string;
  nghia?: string;
  audio?: string;
  curve?: Curve | null;
}

/** Đường cao độ mẫu: semitone theo bước `hop` giây, `null` = khung không có thanh. */
export interface Curve {
  hop: number;
  dur: number;
  st: (number | null)[];
}

interface BlockBase {
  id: string;
  kieu: string;
  tieu_de?: string;
}

// ─── Khối lõi (mọi môn) ──────────────────────────────────────────────────

export interface VanBanBlock extends BlockBase {
  kieu: "van_ban";
  noi_dung: string;
}
export interface HinhAnhBlock extends BlockBase {
  kieu: "hinh_anh";
  anh: string;
  chu_thich?: string;
}
export interface DocBlock extends BlockBase {
  kieu: "doc";
  the: { tieu_de?: string; noi_dung: string }[];
}
export interface TracNghiemQuestion {
  hoi: string;
  lua_chon: string[];
  dap_an: number;
  giai_thich?: string;
  audio?: string;
  chu_lon?: string;
  hien?: WordObj;
}
export interface TracNghiemBlock extends BlockBase {
  kieu: "trac_nghiem";
  tinh_diem?: boolean;
  cau: TracNghiemQuestion[];
}
export interface TheTuBlock extends BlockBase {
  kieu: "the_tu";
  tu: WordObj[];
}

// ─── Khối ngôn ngữ ───────────────────────────────────────────────────────

export interface HoiThoaiLine extends WordObj {
  vai: string;
}
export type HoiThoaiMode = "xem" | "noi_duoi" | "nhap_vai";
export interface HoiThoaiBlock extends BlockBase {
  kieu: "hoi_thoai";
  che_do: HoiThoaiMode[];
  an_phu_de?: boolean;
  canh: {
    ten: string;
    boi_canh?: string;
    nhan_vat: { id: string; ten: string; vai?: string }[];
    cau: HoiThoaiLine[];
  };
}
export interface NgheChonThanhBlock extends BlockBase {
  kieu: "nghe_chon_thanh";
  cau: {
    audio: string;
    giong?: string;
    chu: string;
    py: string;
    thanh: 1 | 2 | 3 | 4;
    nghia?: string;
  }[];
}
export interface NoiThanhItem {
  chu: string;
  py: string;
  thanh: 1 | 2 | 3 | 4;
  audio: string;
  curve: Curve;
}
export interface NoiThanhBlock extends BlockBase {
  kieu: "noi_thanh";
  cham?: string;
  cau: NoiThanhItem[];
}
export interface NoiCumBlock extends BlockBase {
  kieu: "noi_cum";
  cham?: string;
  cau: WordObj[];
}
export interface ChuHanBlock extends BlockBase {
  kieu: "chu_han";
  chu: {
    chu: string;
    phon?: string;
    hv?: string;
    thanh_phan?: { phan: string; giai_nghia: string }[];
    meo?: string;
  }[];
}
export interface VietChuBlock extends BlockBase {
  kieu: "viet_chu";
  nguon_net?: string;
  // `net`: dữ liệu nét định dạng hanzi-writer-data ({strokes, medians, …}).
  chu: { chu: string; net: { strokes: string[]; medians: number[][][]; radStrokes?: number[] } }[];
}
export interface SoDemBlock extends BlockBase {
  kieu: "so_dem";
  so: { so: number; chu: string; py?: string; thanh?: number; audio?: string }[];
}
export interface NgheSoBlock extends BlockBase {
  kieu: "nghe_so";
  cau: { audio: string; dap_an: string }[];
}

export type ContentBlock =
  | VanBanBlock
  | HinhAnhBlock
  | DocBlock
  | TracNghiemBlock
  | TheTuBlock
  | HoiThoaiBlock
  | NgheChonThanhBlock
  | NoiThanhBlock
  | NoiCumBlock
  | ChuHanBlock
  | VietChuBlock
  | SoDemBlock
  | NgheSoBlock;

export interface ContentLessonPayload {
  schema: string;
  lesson_id: string;
  order: number;
  title_vi: string;
  language?: string;
  duration_min?: number;
  buoi_goi_y?: number;
  /** zh: {phien_am: ["pinyin","han_viet"], phon_the: true} → thanh chọn hiển thị. */
  hien_thi?: { phien_am?: string[]; phon_the?: boolean };
  muc_tieu?: string[];
  video?: { duration_s?: number; giang_vien?: string; loi_giang?: string[] };
  blocks: ContentBlock[];
}

export function isContentLesson(payload: unknown): payload is ContentLessonPayload {
  return (
    typeof payload === "object" &&
    payload !== null &&
    typeof (payload as { schema?: unknown }).schema === "string" &&
    (payload as { schema: string }).schema.startsWith(CONTENT_LESSON_SCHEMA_PREFIX) &&
    Array.isArray((payload as { blocks?: unknown }).blocks)
  );
}

/** Props chung của mọi component khối. */
export interface BlockProps<B extends ContentBlock = ContentBlock> {
  block: B;
  lessonSlug: string;
  language: string;
}
